import { useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { SectionTitle, fmtDate } from "./PilotBits";
import { MemberAvatar } from "./PilotTeam";
import { TASK_STATUS_LABELS, PRIORITY_LABELS, isTaskOverdue, priorityBadgeClass, taskStatusBadgeClass } from "../lib/tasks";
import { LIMITED_TASKS_NOTICE, TEAM_TASK_FILTERS, assignableMembers, organizeTeamTasks } from "../lib/pilots";
import { AlertTriangle, ArrowRightLeft, Loader2, Lock, Plus, RefreshCw } from "lucide-react";


function TaskLine({ task, onOpen, onReassign, canReassign }) {
  const overdue = isTaskOverdue(task);
  const status = task.status || (task.done ? "termine" : "a_faire");
  return (
    <li className="flex items-center gap-2 py-2" data-testid={`team-task-${task.id}`} data-mine={task.is_mine} data-editable={task.can_edit}>
      <button type="button" onClick={() => onOpen(task.id)} className="min-w-0 flex-1 text-left" data-testid={`team-task-open-${task.id}`}>
        <div className={`text-sm truncate ${task.done ? "line-through text-slate-400" : "text-slate-900"}`}>{task.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs">
          <span className={`font-medium ${overdue ? "text-red-600" : "text-slate-500"}`}>{task.due_date ? `Échéance ${fmtDate(task.due_date)}` : "Sans échéance"}</span>
          <span className={`rounded-full px-2 py-0.5 font-semibold ${priorityBadgeClass(task.priority)}`}>{PRIORITY_LABELS[task.priority] || task.priority}</span>
          <span className={`rounded-full px-2 py-0.5 font-semibold ${taskStatusBadgeClass(status)}`}>{overdue ? "En retard" : TASK_STATUS_LABELS[status] || status}</span>
        </div>
      </button>
      {!task.can_edit && <span title="Tâche d'un collègue : lecture seule" aria-label="Lecture seule" data-testid={`team-task-lock-${task.id}`}><Lock size={13} className="text-slate-300" /></span>}
      {canReassign && task.can_edit && !task.done && (
        <Button variant="ghost" size="sm" className="shrink-0 h-8 px-2 text-slate-500" onClick={() => onReassign(task)} aria-label="Réattribuer" data-testid={`team-task-reassign-${task.id}`}><ArrowRightLeft size={14} /></Button>
      )}
    </li>
  );
}

// The team's pilots-domain tasks: everyone reads everything (descriptions included, in the detail), each person changes only their own.
export default function PilotTasksCard({ pilot, teamTasks, filter, onFilter, canCreate, onCreate, onOpen, onReassign, onRetry }) {
  const { groups, counts, tasks } = useMemo(() => organizeTeamTasks(teamTasks.items, filter), [teamTasks.items, filter]);
  const canReassign = assignableMembers(pilot).length > 1;
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-team-tasks-card">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle hint={`${counts.open} ouverte${counts.open > 1 ? "s" : ""}`}>Tâches de l'équipe</SectionTitle>
        {canCreate && <Button variant="outline" size="sm" className="shrink-0 -mt-1" onClick={onCreate} data-testid="team-tasks-create"><Plus size={14} className="mr-1" />Nouvelle tâche</Button>}
      </div>
      <p className="text-xs text-slate-500 -mt-1 mb-2" data-testid="team-tasks-hint">{LIMITED_TASKS_NOTICE}</p>
      <div className="flex gap-1.5 mb-2" role="tablist" aria-label="Filtre des tâches">
        {TEAM_TASK_FILTERS.map((f) => (
          <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} onClick={() => onFilter(f.id)} data-testid={`team-tasks-filter-${f.id}`}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${filter === f.id ? "bg-jamora text-white border-jamora" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {f.label} ({f.id === "mine" ? counts.mine : counts.all})
          </button>
        ))}
      </div>

      {teamTasks.status === "loading" && <div className="space-y-2" data-testid="team-tasks-loading"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>}
      {teamTasks.status === "error" && (
        <div className="text-center py-4 space-y-2" data-testid="team-tasks-error">
          <AlertTriangle className="mx-auto text-amber-500" size={18} />
          <p className="text-sm text-slate-600">Impossible de charger les tâches de l'équipe.</p>
          <Button variant="outline" size="sm" onClick={onRetry}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      )}
      {teamTasks.status === "ready" && tasks.length === 0 && (
        <p className="text-sm text-slate-500 py-3" data-testid="team-tasks-empty">{filter === "mine" ? "Aucune tâche ne vous est assignée sur ce pilote." : "Aucune tâche pour le moment."}</p>
      )}
      {teamTasks.status === "ready" && tasks.length > 0 && (
        <div className="space-y-3" data-testid="team-tasks-list">
          {groups.map((g) => (
            <div key={g.key}>
              {filter !== "mine" && (
                <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-400 mb-0.5" data-testid="team-tasks-group">
                  {(g.tasks[0].assignees || []).slice(0, 3).map((a) => <MemberAvatar key={a.id} member={a} size={18} />)}
                  <span className="truncate">{g.mine ? `${g.label} (vous)` : g.label}</span>
                </div>
              )}
              <ul className="divide-y divide-slate-100">{g.tasks.map((t) => <TaskLine key={t.id} task={t} onOpen={onOpen} onReassign={onReassign} canReassign={canReassign} />)}</ul>
            </div>
          ))}
        </div>
      )}
      {teamTasks.truncated && <p className="text-xs text-slate-400 mt-2">Seules les 500 premières tâches sont affichées.</p>}
    </Card>
  );
}

// Hand one of MY open tasks to a colleague of the team (the backend only accepts active people of the team).
export function ReassignDialog({ open, task, pilot, busy, onClose, onSubmit }) {
  const [target, setTarget] = useState("");
  const candidates = assignableMembers(pilot).filter((m) => !task || !(task.assigned_to || []).includes(m.id));
  const close = () => { setTarget(""); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>Réattribuer la tâche</DialogTitle>
          <DialogDescription>« {task?.title} » passera à la personne choisie, qui en sera prévenue. Vous la retrouverez dans les tâches de l'équipe, en lecture seule.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reassign-select">Nouveau responsable</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger id="reassign-select" data-testid="reassign-select"><SelectValue placeholder="Choisir un membre de l'équipe" /></SelectTrigger>
            <SelectContent>{candidates.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={close} disabled={busy}>Annuler</Button>
          <Button  disabled={!target || busy} onClick={async () => { if (await onSubmit(task, target)) close(); }} data-testid="reassign-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Réattribuer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
